import React, { createContext, memo, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useTheme } from 'styled-components';
import { getColor } from '../../../src/styled/utils';
import { Icon } from '../icon';
import { IconButton } from '../icon-button';
import { Body2 } from '../typography';
import { MessageWrapper, NotificationWrapper } from './styled';
const CONTAINER_ID = 'invent-kit-container-notifications';
const CloseButtonContext = createContext({
    closeButtonState: undefined,
    setCloseButtonState: () => {
        console.error('Default CloseButton State should be overwritten');
    }
});
/**
 * Props for the toast component used to display notifications.
 * @typedef {Object} ToastComponentPropsT
 * @property {InfoNotificationTypeT} type - The type of the notification.
 * @property {ToastContent} text - The content of the notification.
 * @property {ToastContentProps<unknown>['closeToast']} [onClose] - Callback function to close the notification.
 */
function getIconNameByType(type) {
    const icons = {
        info: 'info_outline',
        success: 'check_outline',
        warning: 'warning_amber',
        error: 'close_outline'
    };
    return icons[type];
}
function useColorByType(type) {
    const theme = useTheme();
    const colors = {
        info: getColor('primary')({ theme }),
        success: getColor('success')({ theme }),
        warning: getColor('warning')({ theme }),
        error: getColor('danger')({ theme })
    };
    return colors[type];
}
const renderCloseButton = (onClose, closeButtonState) => {
    var _a;
    const handleClose = onClose !== null && onClose !== void 0 ? onClose : (() => {
        console.error('No onClose function provided for notification');
    });
    if (closeButtonState === undefined || closeButtonState === true) {
        return (React.createElement(IconButton, { onClick: onClose },
            React.createElement(Icon, { name: "close" })));
    }
    if (React.isValidElement(closeButtonState)) {
        const originalOnClick = (_a = closeButtonState.props) === null || _a === void 0 ? void 0 : _a.onClick;
        return React.cloneElement(closeButtonState, {
            onClick: () => {
                if (originalOnClick) {
                    originalOnClick();
                }
                handleClose();
            }
        });
    }
    if (typeof closeButtonState === 'function') {
        return closeButtonState({ onClick: onClose });
    }
    return null;
};
export const Message = ({ type, content, onClose }) => {
    const color = useColorByType(type);
    const iconName = useMemo(() => getIconNameByType(type), [type]);
    const contentMsg = typeof content === 'string' ? React.createElement(Body2, null, content) : content;
    const { closeButtonState } = useContext(CloseButtonContext);
    return (React.createElement(MessageWrapper, null,
        React.createElement(Icon, { size: "medium", color: color, name: iconName }),
        contentMsg,
        renderCloseButton(onClose, closeButtonState)));
};
export const showNotification = (type, content, options) => {
    const { ToastComponent, ...restOptions } = options !== null && options !== void 0 ? options : {};
    const Component = ToastComponent ? ToastComponent : Message;
    return toast(({ closeToast, ...props }) => (React.createElement(Component, { type: type, content: content, onClose: closeToast, ...props })), {
        containerId: CONTAINER_ID,
        ...restOptions,
        type,
        icon: false,
        closeButton: false
    });
};
export const updateNotification = (id, type, content, options) => {
    const { ToastComponent, ...restOptions } = options !== null && options !== void 0 ? options : {};
    const Component = ToastComponent ? ToastComponent : Message;
    toast.update(id, {
        render: ({ closeToast, ...props }) => (React.createElement(Component, { type: type, content: content, onClose: closeToast, ...props })),
        containerId: CONTAINER_ID,
        ...restOptions,
        type,
        icon: false,
        closeButton: false
    });
};
export const hideNotification = (id) => {
    toast.dismiss(id);
};
// FIX: When an invalid type is passed, the close button take the whole width of the notification after text content.
// Solution: If an invalid type is passed, the default type should be used.
export const NotificationsContainer = memo((props) => {
    const [closeButtonState, setCloseButtonState] = useState(undefined);
    useEffect(() => {
        setCloseButtonState(props.closeButton);
    }, [props]);
    return (React.createElement(CloseButtonContext.Provider, { value: { closeButtonState, setCloseButtonState } },
        React.createElement(NotificationWrapper, { enableMultiContainer: true, containerId: CONTAINER_ID, autoClose: 3000, limit: 3, position: toast.POSITION.BOTTOM_LEFT, closeButton: false, hideProgressBar: true, ...props })));
});
